import { ArrowLeftRight, RefreshCw, Loader2 } from "lucide-react";
import { useFxRates } from "../lib/useFxRates.jsx";

/* ============================================================
   CurrencyWidget
   ============================================================
   Compact strip showing live JPY → BRL and JPY → USD rates.
   Placed on the homepage to give recurring visitors a reason 
   to come back. Brazilian community checks JPY/BRL constantly.
   ============================================================ */

export default function CurrencyWidget() {
  const { rates, loading, error, refetch } = useFxRates();

  // Reference amount for the bigger conversion
  const refJPY = 100000;
  const brlValue = (refJPY * rates.JPY_TO_BRL).toLocaleString("pt-BR", {
    maximumFractionDigits: 0,
  });
  const usdValue = (refJPY * rates.JPY_TO_USD).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });

  const isLive = rates.source !== "fallback";
  const updatedLabel = isLive && rates.updated
    ? formatRelativeDate(rates.updated)
    : "câmbio aproximado";

  return (
    <div className="bg-surface border border-border rounded-2xl p-5 md:p-6 mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-ink-soft font-semibold">
          <ArrowLeftRight size={14} />
          Câmbio agora
        </div>
        <button
          onClick={refetch}
          disabled={loading}
          aria-label="Atualizar câmbio"
          className="p-1.5 rounded-md text-ink-soft hover:text-ink hover:bg-border/40 transition-colors disabled:opacity-40"
        >
          {loading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <RateBlock
          label="1 iene"
          value={`R$ ${rates.JPY_TO_BRL.toFixed(5)}`}
          sub={`¥${refJPY.toLocaleString("ja-JP")} = R$ ${brlValue}`}
          highlight
        />
        <RateBlock
          label="1 iene em dólar"
          value={`$ ${rates.JPY_TO_USD.toFixed(5)}`}
          sub={`¥${refJPY.toLocaleString("ja-JP")} = $ ${usdValue}`}
        />
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border text-[11px] font-mono text-ink-soft">
        <span>
          {isLive ? "Fonte: ECB via frankfurter.app" : "Fonte: cache local"}
        </span>
        <span>{updatedLabel}</span>
      </div>

      {error && (
        <div className="mt-3 text-[11px] text-ink-soft italic">
          Não foi possível atualizar agora. Usando última cotação salva.
        </div>
      )}
    </div>
  );
}

function RateBlock({ label, value, sub, highlight }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-soft font-mono mb-1">
        {label}
      </div>
      <div
        className={`font-serif text-3xl md:text-[34px] font-medium leading-none tabular-nums ${
          highlight ? "text-accent" : "text-ink"
        }`}
      >
        {value}
      </div>
      <div className="text-sm text-ink-soft font-mono mt-1.5 tabular-nums">
        {sub}
      </div>
    </div>
  );
}

function formatRelativeDate(isoDate) {
  // frankfurter returns "2026-05-15"
  try {
    const d = new Date(isoDate);
    const now = new Date();
    const diffMs = now - d;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays >= 2) return `há ${diffDays} dias`;
    if (diffDays === 1) return "ontem";
    if (diffHours >= 1) return `há ${diffHours}h`;
    return "agora";
  } catch (_) {
    return isoDate;
  }
}
